import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import FormFieldInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-interface';
import FormFieldOptionInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-option-interface';
import FormFieldSetInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-set-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import Techspec from '@/model/entity/job/techspec';
import TechspecDto from '@/model/entity/job/techspec-dto';

@Component
export default class TechspecForm extends Vue
{
    @Prop()
    private techspec!: Techspec;

    @Prop({
        type: Boolean,
        default: false,
    })
    private saving!: boolean;

    private form: FormInterface|null = null;

    private created()
    {
        this.generateFormFromTechspec();
    }

    @Watch('techspec')
    private onTechspecChanged()
    {
        this.generateFormFromTechspec();
    }

    /**
     * Handles form saves
     *
     * @param form
     */

    private async onSave(form: FormInterface)
    {
        if (!this.techspec)
        {
            return;
        }

        // Generate a techspec from the form data
        const techspec = this.generateTechspecFromForm(form);

        // Emit a save event with the techspec
        this.$emit('save', techspec);
    }

    /**
     * Generates the form from the techspec
     */

    private generateFormFromTechspec()
    {
        if (!this.techspec)
        {
            this.form = null;
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    title: this.$t('general.field.basic_details').toString(),
                    fields: [
                        {
                            key: 'client',
                            type: 'integer',
                            title: this.$t('general.field.client').toString(),
                            value: this.techspec.clientId,
                            required: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.clientService.clientFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.clientService.clientFormTitlesCallback(values),
                            disableInitialOptionsCallback: true,
                            disableEmptySearchResults: true,
                        },
                        {
                            key: 'manager',
                            type: 'string',
                            title: this.$t('general.field.manager').toString(),
                            value: this.techspec.managerId as string,
                            required: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.userService.formOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.userService.formTitlesCallback(values),
                        },
                        {
                            key: 'type',
                            type: 'integer',
                            title: this.$t('general.field.type').toString(),
                            value: this.techspec.typeId,
                            required: true,
                            optionsCallback: async(search: string) =>
                            {
                                const techspecTypes = await this.$serviceContainer.techspecService.findTechspecTypes({
                                    searchTerm: search,
                                    enabled: true,
                                    order: [
                                        {
                                            key: 'title',
                                            descending: false,
                                        },
                                    ],
                                });

                                const options: FormFieldOptionInterface[] = [];

                                for (const techspecType of techspecTypes.results)
                                {
                                    options.push({
                                        value: techspecType.id,
                                        title: techspecType.title,
                                    });
                                }

                                return options;
                            },
                            titlesCallback: async(values: string[]) =>
                            {
                                const techspecTypes = await this.$serviceContainer.techspecService.findTechspecTypes({
                                    id: values,
                                    order: [
                                        {
                                            key: 'title',
                                            descending: false,
                                        },
                                    ],
                                });

                                const options: Record<string, string> = {};

                                for (const techspecType of techspecTypes.results)
                                {
                                    if (!techspecType.id)
                                    {
                                        continue;
                                    }

                                    options[techspecType.id] = techspecType.title;
                                }

                                return options;
                            },
                        },
                        {
                            key: 'name',
                            type: 'string',
                            title: this.$t('general.field.name').toString(),
                            value: this.techspec.name,
                            required: true,
                        },
                        {
                            key: 'description',
                            type: 'string',
                            title: this.$t('general.field.description').toString(),
                            value: this.techspec.description,
                            required: false,
                            multiline: true,
                            rich: true,
                            markdown: true,
                        },
                        {
                            key: 'project',
                            type: 'boolean',
                            title: this.$t('general.field.project').toString(),
                            value: this.techspec.project,
                            required: true,
                        },
                        {
                            key: 'active',
                            type: 'boolean',
                            title: this.$t('general.field.active').toString(),
                            value: this.techspec.active,
                            required: true,
                        },
                    ],
                    required: true,
                },
                {
                    title: this.$t('general.field.schedule').toString(),
                    fields: [
                        {
                            key: 'dateCreated',
                            type: 'datetime',
                            title: this.$t('general.field.date_created').toString(),
                            value: this.techspec.dto.dateCreated ? this.techspec.dateCreated : null,
                            required: false,
                            readonly: true,
                        },
                        {
                            key: 'deadline',
                            type: 'datetime',
                            title: this.$t('general.field.deadline').toString(),
                            value: this.techspec.deadline,
                            required: false,
                            clearable: true,
                        },
                    ],
                },
            ],
            stepper: true,
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Generates the techspec from the form
     */

    private generateTechspecFromForm(form: FormInterface): Techspec
    {
        if (!this.techspec)
        {
            return new Techspec(new TechspecDto());
        }

        const techspec = new Techspec(cloneDeep(this.techspec.dto));

        this.addFieldValues(techspec, form.items);

        return techspec;
    }

    /**
     * Adds field values from form items to the passed techspec
     *
     * @param techspec
     * @param items
     */

    private addFieldValues(techspec: Techspec, items: (FormFieldSetInterface | FormFieldInterface)[])
    {
        for (const item of items)
        {
            if (!('key' in item))
            {
                this.addFieldValues(techspec, item.fields);
                continue;
            }

            switch (item.key)
            {
            case 'client':
                techspec.clientId = item.value as number;
                break;

            case 'manager':
                techspec.managerId = item.value as string;
                break;

            case 'type':
                techspec.typeId = item.value as number;
                break;

            case 'name':
                techspec.name = item.value as string;
                break;

            case 'description':
                techspec.description = item.value as string;
                break;

            case 'deadline':
                techspec.deadline = item.value as Date;
                break;

            case 'project':
                techspec.project = item.value as boolean;
                break;

            case 'active':
                techspec.active = item.value as boolean;
                break;
            }
        }
    }
}